import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Container from '@/components/foundations/content';
import { StaticImage } from 'gatsby-plugin-image';
import * as styles from './service.module.scss';

import ResearchSvg from '@/svgs/handwriting/research';
import AnalyzeSvg from '@/svgs/handwriting/analyze';
import CreateSvg from '@/svgs/handwriting/create';
import AssessmentSvg from '@/svgs/handwriting/assessment';
import InfoSvg from '@/svgs/icon/info';

const ServiceContent: React.FC = ({ inView }: any) => {
  SwiperCore.use([Autoplay]);
  const [swiper, setSwiper] = useState<SwiperCore | null>(null);
  const [slideIndexState, setSlideIndexState] = useState<number>(0);

  const controllerStyle = classNames(styles.oService__controller);

  const controllerChildStyle = [...Array(4)].map((number, index) => {
    return classNames(slideIndexState === index && styles.active);
  });

  const swiperChange = (swiper: SwiperCore) => {
    setSlideIndexState(swiper.realIndex);
  };

  const controllerOnClick = (number: number) => {
    swiper && swiper.slideToLoop(number);
  };

  useEffect(() => {
    if (inView && swiper) {
      swiper.slideToLoop(0);
      swiper.autoplay.start();
    }
  }, [inView]);

  return (
    <>
      <h2>
        <span>
          <strong>Service</strong>
          <small>提供内容</small>
        </span>
      </h2>
      <h3>人間中心設計プロセスに準拠し、目的とご予算に合わせた最適なアプローチをご提案します。</h3>
      <div className={styles.oService__slider}>
        <ul className={controllerStyle}>
          <li className={controllerChildStyle[0]} onClick={() => controllerOnClick(0)}>
            調査
          </li>
          <li className={controllerChildStyle[1]} onClick={() => controllerOnClick(1)}>
            分析
          </li>
          <li className={controllerChildStyle[2]} onClick={() => controllerOnClick(2)}>
            作成
          </li>
          <li className={controllerChildStyle[3]} onClick={() => controllerOnClick(3)}>
            評価
          </li>
        </ul>
        <div className={styles.oService__sliderMain}>
          <Swiper
            className={styles.oService__swiper}
            autoplay={true}
            loop={true}
            loopedSlides={2}
            onActiveIndexChange={swiperChange}
            onSwiper={setSwiper}
          >
            <SwiperSlide className={styles.oService__slider__slide}>
              <h4>ユーザー調査</h4>
              <p>
                ターゲットとされる
                <strong>ユーザーが、何のためにどんな気持ちでサービスを利用するか</strong>
                を明らかにし、調査結果から仮説を抽出。ユーザーの理想的な行動を、シナリオとして書き起こしていきます。
              </p>
              <figure className={styles.oService__slider__slide__decoration}>
                <ResearchSvg />
              </figure>
            </SwiperSlide>
            <SwiperSlide className={styles.oService__slider__slide}>
              <h4>シナリオ分析</h4>
              <p>
                ユーザーシナリオの中で、
                <strong>どのようなシチュエーションで、どのような機能・インターフェースを望んでいるのか</strong>
                を分析し、サービス価値へと繋がる設計を行っていきます。
              </p>
              <figure className={styles.oService__slider__slide__decoration}>
                <AnalyzeSvg />
              </figure>
            </SwiperSlide>
            <SwiperSlide className={styles.oService__slider__slide}>
              <h4>モックアップ作成</h4>
              <p>
                <strong>設計したコンセプトデザインの視覚化</strong>
                を行っていきます。各画面の比較・評価から、新たな問題点や要望に対するアイデアを反映。より洗練されたものに仕上げていきます。
              </p>
              <figure className={styles.oService__slider__slide__decoration}>
                <CreateSvg />
              </figure>
            </SwiperSlide>
            <SwiperSlide className={styles.oService__slider__slide}>
              <h4>妥当性/有用性の評価</h4>
              <p>
                <strong>ユーザーシナリオの妥当性、製品・サービスの有用性を確認</strong>
                していきます。ヒューリスティック評価などから、デザイン案が「ユーザーの期待に応えているか」「使いやすいか」などで評価し、本開発前の最適な仕様を精査します。
              </p>
              <figure className={styles.oService__slider__slide__decoration}>
                <AssessmentSvg />
              </figure>
            </SwiperSlide>
          </Swiper>
          <Character index={slideIndexState} />
        </div>
      </div>
      <p className={styles.oService__attention}>
        <InfoSvg />
        デザインツールを使用したUIプロトタイピングを指していますが、開発のご相談も歓迎いたします。
      </p>
    </>
  );
};

const Character: React.FC<{ index: number }> = ({ index }) => {
  interface mappingType {
    [key: string]: String;
  }

  const mapping: mappingType = {
    1: styles.visible1,
    2: styles.visible2,
    3: styles.visible3,
    4: styles.visible4,
  };

  const style = classNames(styles.oService__charactor, mapping[`${index + 1}`]);
  return (
    <ul className={style}>
      <li>
        <StaticImage src="./img/research.png" alt="調査" />
      </li>
      <li>
        <StaticImage src="./img/analyze.png" alt="分析" />
      </li>
      <li>
        <StaticImage src="./img/create.png" alt="作成" />
      </li>
      <li>
        <StaticImage src="./img/assessment.png" alt="評価" />
      </li>
    </ul>
  );
};

const Conversion: React.FC = () => {
  return (
    <Container
      className={{ wrapper: styles.oService, inner: styles.lContent__inner }}
      intersection={{ visible: styles.visible, standby: styles.standby }}
    >
      <ServiceContent />
    </Container>
  );
};

export default Conversion;
